import React, { useEffect, useState } from 'react';

interface AnimatedCollapseProps {
  /** Whether the content should be shown or hidden */
  shouldShow: boolean;
  /** The content to be animated */
  children: React.ReactNode;
  /** Animation duration in milliseconds (default: 300) */
  duration?: number;
  /** Animation direction: 'width' | 'height' | 'both' (default: 'width') */
  direction?: 'width' | 'height' | 'both';
  /** Custom CSS classes to apply to the container */
  className?: string;
  /** Callback function called when animation completes */
  onAnimationComplete?: (isVisible: boolean) => void;
  /** Whether to preserve flex properties of child (default: false) */
  preserveFlex?: boolean;
  /**
   * The flex basis percentage when expanded (default: auto-detect from flex-grow)
   * Use 'auto' to auto-detect, or specify like '33.33%', '50%', '100%' etc.
   */
  flexBasisExpanded?: string | 'auto';
}

const AnimatedCollapse: React.FC<AnimatedCollapseProps> = ({
  shouldShow,
  children,
  duration = 300,
  direction = 'width',
  className = '',
  onAnimationComplete,
  preserveFlex = false,
  flexBasisExpanded = 'auto',
}) => {
  const [isVisible, setIsVisible] = useState(shouldShow);
  const [isCollapsing, setIsCollapsing] = useState(false);
  const [detectedFlexBasis, setDetectedFlexBasis] = useState<string>('100%');
  const [initialHeight, setInitialHeight] = useState<number | null>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);

  // Auto-detect flex basis when component mounts and preserveFlex is true
  useEffect(() => {
    if (
      preserveFlex &&
      flexBasisExpanded === 'auto' &&
      containerRef.current &&
      isVisible &&
      !isCollapsing
    ) {
      const container = containerRef.current;
      const parent = container.parentElement;

      if (parent) {
        const parentStyle = window.getComputedStyle(parent);
        const isFlexParent =
          parentStyle.display === 'flex' ||
          parentStyle.display === 'inline-flex';

        if (isFlexParent) {
          // Count flex children to calculate proportion
          const flexChildren = Array.from(parent.children).filter((child) => {
            const childStyle = window.getComputedStyle(child);
            return childStyle.flex !== 'none' && childStyle.flexGrow !== '0';
          });

          const flexGrowSum = flexChildren.reduce((sum, child) => {
            const childStyle = window.getComputedStyle(child);
            const flexGrow = parseFloat(childStyle.flexGrow) || 1;
            return sum + flexGrow;
          }, 0);

          const containerStyle = window.getComputedStyle(container);
          const containerFlexGrow = parseFloat(containerStyle.flexGrow) || 1;

          const percentage = (containerFlexGrow / flexGrowSum) * 100;
          setDetectedFlexBasis(`${percentage.toFixed(2)}%`);
        }
      }
    }
  }, [preserveFlex, flexBasisExpanded, isVisible, isCollapsing]);

  useEffect(() => {
    if (!shouldShow && isVisible) {
      // Capture initial height before starting collapse for width animations
      if (direction === 'width' && containerRef.current) {
        const height = containerRef.current.getBoundingClientRect().height;
        setInitialHeight(height);
      }

      // Start collapse animation
      setIsCollapsing(true);
      // Hide after animation completes
      const timer = setTimeout(() => {
        setIsVisible(false);
        setIsCollapsing(false);
        setInitialHeight(null);
        onAnimationComplete?.(false);
      }, duration);
      return () => {
        clearTimeout(timer);
      };
    } else if (shouldShow && !isVisible) {
      // Show immediately when shouldShow becomes true
      setIsVisible(true);
      setIsCollapsing(false);
      setInitialHeight(null);
      onAnimationComplete?.(true);
    }
  }, [shouldShow, isVisible, duration, onAnimationComplete, direction]);

  if (!isVisible) {
    return null;
  }

  const getAnimationClasses = (): string => {
    const baseClasses = 'ease-in-out';
    const flexClasses = preserveFlex ? 'flex-1' : '';

    // Use overflow-hidden during width animations to prevent content reflow
    const overflowClasses =
      direction === 'width' && isCollapsing
        ? 'overflow-hidden'
        : 'overflow-visible';

    let dimensionClasses = '';
    if (isCollapsing) {
      switch (direction) {
        case 'width':
          dimensionClasses = 'opacity-0';
          break;
        case 'height':
          dimensionClasses = 'h-0 opacity-0';
          break;
        case 'both':
          dimensionClasses = 'h-0 opacity-0';
          break;
      }
    } else {
      switch (direction) {
        case 'width':
          dimensionClasses = 'opacity-100';
          break;
        case 'height':
          dimensionClasses = 'h-auto opacity-100';
          break;
        case 'both':
          dimensionClasses = 'h-auto opacity-100';
          break;
      }
    }

    return `${baseClasses} ${overflowClasses} ${dimensionClasses} ${flexClasses} ${className}`.trim();
  };

  const getInlineStyles = (): React.CSSProperties => {
    const baseTransition = {
      transitionProperty: 'opacity, flex-basis, min-width, width, height',
      transitionDuration: `${duration}ms`,
      transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
    };

    if (direction === 'height') {
      return baseTransition;
    }

    // For width animations, fix the height to prevent content reflow
    const heightStyles =
      direction === 'width' && isCollapsing && initialHeight
        ? { height: `${initialHeight}px`, overflow: 'hidden' }
        : {};

    if (preserveFlex) {
      // Use specified flex basis or auto-detected value
      const expandedBasis =
        flexBasisExpanded === 'auto' ? detectedFlexBasis : flexBasisExpanded;

      return {
        ...baseTransition,
        ...heightStyles,
        flexBasis: isCollapsing ? '0%' : expandedBasis,
        minWidth: isCollapsing ? '0px' : 'auto',
      };
    } else {
      // For non-flex containers, animate width
      return {
        ...baseTransition,
        ...heightStyles,
        width: isCollapsing ? '0%' : '100%',
      };
    }
  };

  return (
    <div
      ref={containerRef}
      className={getAnimationClasses()}
      style={getInlineStyles()}
    >
      {children}
    </div>
  );
};

export default AnimatedCollapse;
